import {
  DEFAULT_DEMO_CONFIG,
  DEFAULT_DEMO_ID,
  DEMO_EXCHANGE_RATES,
  DEMO_FEATURE_CONFIGS,
} from '@/demo/defaultDemoConfig';
import {
  buildCreditNotes,
  buildDemoSupplier,
  buildInvoiceRules,
  buildLockedInvoiceStats,
  buildLockedInvoices,
  buildMarketingArtifacts,
  buildOffers,
  buildPlayableInvoiceStats,
  buildPlayableInvoices,
  buildPricingSuggestions,
  buildRecentClears,
  buildSupplierMarketClears,
  buildSupplierMarketClearsStats,
  buildSupplierMarkets,
  buildUserSuppliers,
  buildUserTerms,
} from '@/demo/demoTransform';
import { MswData, PricingSuggestionsResponse } from '@/test/mocks/sharedHandlers';

/**
 * Shape of the demo config returned by GET /api/admin/demo-configs/:uuid.
 *
 * These entity interfaces mirror the JSON the config app produces (organizations, divisions,
 * markets, takerMarkets, supplier, ...). The demo MSW layer joins them into the SEA API
 * responses the dashboard fetches — see demoTransform. The Admin API does not exist yet
 * (SUPP-18862), so fetchDemoConfig parses the config from the `demo` URL hash or query param
 * (base64-encoded JSON) instead of fetching by UUID, falling back to DEFAULT_DEMO_CONFIG when
 * absent or invalid.
 */
export interface DemoOrganization {
  id: number;
  uuid: string;
  name: string;
  type: 'supplier' | 'buyer';
  country?: string;
  currency?: string;
  industry?: string;
  logoUrl?: string | null;
}

export interface DemoDivisionAddress {
  addressLine?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
}

export interface DemoDivision {
  id: number;
  uuid: string;
  name: string;
  organizationId: number;
  organizationUuid: string;
  type: 'supplier' | 'buyer';
  address?: DemoDivisionAddress;
  employeeCount?: number;
  naics?: string;
}

/**
 * Supported pricing/market types for a demo market. `DemoMarket.type` is typed as a loose
 * `string` because it round-trips through an untrusted boundary (base64 JSON in the URL) —
 * use {@link normalizePricingType} wherever that raw value needs to be treated as one of these.
 */
export const DEMO_PRICING_TYPES = ['price-discovery', 'static', 'benchmark'] as const;
export type DemoPricingType = (typeof DEMO_PRICING_TYPES)[number];

function isDemoPricingType(value: string): value is DemoPricingType {
  return (DEMO_PRICING_TYPES as readonly string[]).includes(value);
}

/**
 * Normalizes an untrusted market `type` string to a supported {@link DemoPricingType}, falling
 * back to 'price-discovery' for anything unrecognized (e.g. a stale or hand-edited config URL)
 * instead of letting an invalid value get cast into the type elsewhere.
 */
export function normalizePricingType(value: string | null | undefined): DemoPricingType {
  return value != null && isDemoPricingType(value) ? value : 'price-discovery';
}

export interface DemoMarket {
  id: number;
  uuid: string;
  name: string;
  makerId: number;
  makerUuid: string;
  makerOrganizationUuid: string;
  type: string;
  currency: string;
  isEnabled: boolean;
  /** True when 3rd party (DSF) funding is enabled for this market. */
  isDsf?: boolean;
  marketType: string;
  nextClearDate: string | null;
  payDate: string | null;
  cashpoolSize: number;
  maxApr: number | null;
  maxDiscount: number | null;
  trendingRate: number | null;
  expressAcceptRate: number | null;
  rateType: 'APR' | 'DISC';
  productType: string;
  rateName?: string | null;
  referenceRate?: number | null;
  spread?: number | null;
}

export interface DemoTakerMarket {
  id: number;
  marketUuid: string;
  marketId: number;
  takerUuid: string;
  takerId: number;
  makerUuid: string;
  makerId: number;
  makerOrganizationUuid: string;
  makerOrganizationId: number;
  makerDivisionName: string;
  makerOrganizationName: string;
  takerOrganizationId: number;
  takerDivisionName: string;
  takerOrganizationName: string;
  currency: string;
  takerMakerId: string;
  eligibleInvoiceCount: number;
  eligibleInvoiceAmount: number;
  eligibleDpeWeightedAverage: number;
  eligibleDpeWeightedSum: number;
  acceptedInvoiceCount: number;
  acceptedInvoiceAmount: number;
  awardedInvoiceCount: number;
  awardedInvoiceAmount: number;
  takerExcludedInvoiceCount: number;
  takerExcludedInvoiceAmount: number;
}

export interface DemoSrm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  imageUrl: string;
}

export interface DemoConfigSupplier {
  id: number;
  uuid: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  organizationId: number;
  organizationUuid: string;
  divisionIds?: number[];
  divisionUuids: string[];
  type: 'supplier';
  currency: string;
  language: string;
  region?: string;
  srm?: DemoSrm | null;
}

export interface DemoAppConfig {
  showNpsSurvey?: boolean;
  enableSimulations?: boolean;
  generatedAt?: string;
  version?: string;
  marketingWidget?: string;
  products?: {
    financeIQ?: boolean;
    lendingConnections?: boolean;
  };
  /** When false, pricing suggestions are suppressed via the SUPPRESS_PRICING_SUGGESTIONS feature config. */
  showPricing?: boolean;
}

export interface RegistrationBuyerMarketingConfig {
  domain: string;
  buyerUuid: string;
  buyerDivisionUuid: string;
  makerOrganizationUuid: string;
  buyerName: string;
  imagePng: string | null;
  imageSvg: string | null;
  lockupImage: string | null;
  logoDisplayMode: string;
  defaultLocale: string;
  supportPhone: string | null;
  supportEmail: string | null;
  localeOverrides: Record<string, unknown> | null;
  executiveContactName: string | null;
  executiveContactTitle: string | null;
  isActive: boolean;
}

export interface RegistrationDemoConfig {
  buyerPermalink: string;
  buyerMarketingConfig: RegistrationBuyerMarketingConfig;
  accountLinkingOutcome: string;
}

export interface DemoConfig {
  organizations: DemoOrganization[];
  divisions: DemoDivision[];
  markets: DemoMarket[];
  takerMarkets: DemoTakerMarket[];
  // Invoice-detail endpoints are out of scope this PR; /supplier-markets stats come from takerMarkets.
  supplier: DemoConfigSupplier;
  config?: DemoAppConfig;
  registrationConfig?: RegistrationDemoConfig;
}

const DEMO_EXPERIENCE_PATH = 'api/demo-experience/demo-configs';

interface DemoConfigApiResponse {
  data: {
    uuid: string;
    name: string;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
    config: DemoConfig;
  };
}

/**
 * Fetches the demo config for a given UUID from the demo-experience API.
 * Returns null when the UUID is the bundled-default sentinel or on any fetch error,
 * so callers fall back to DEFAULT_DEMO_CONFIG via resolveDemoConfig.
 */
export interface FetchedDemoConfig {
  config: DemoConfig;
  name: string;
}

export async function fetchDemoConfig(uuid: string): Promise<FetchedDemoConfig | null> {
  if (uuid === DEFAULT_DEMO_ID) {
    return null;
  }

  try {
    const gatewayUrl = import.meta.env.VITE_GATEWAY_URL as string;
    const response = await fetch(`${gatewayUrl}/${DEMO_EXPERIENCE_PATH}/${uuid}`);
    if (!response.ok) {
      return null;
    }

    const body = (await response.json()) as DemoConfigApiResponse;
    return { config: body.data.config, name: body.data.name };
  } catch {
    return null;
  }
}

/** Returns the fetched config or the bundled default — single resolution point for callers. */
export function resolveDemoConfig(fetched: FetchedDemoConfig | null): DemoConfig {
  return fetched?.config ?? DEFAULT_DEMO_CONFIG;
}

/**
 * Resolves the MSW data payload the shared handlers serve. A fetched config is joined into SEA
 * responses via demoTransform. Exchange rates and feature configs are demo-owned constants until
 * the config app supplies them.
 *
 * When config.showPricing is false, SUPPRESS_PRICING_SUGGESTIONS is injected into featureConfigs
 * so the dashboard suppresses pricing UI — the same mechanism the real app uses.
 */
export function mapDemoConfigToMswData(config: DemoConfig): MswData {
  const resolved = config;
  const showPricing = resolved.config?.showPricing !== false;

  const featureConfigs = showPricing
    ? DEMO_FEATURE_CONFIGS
    : {
        data: {
          ...DEMO_FEATURE_CONFIGS.data,
          featureConfigs: [
            ...DEMO_FEATURE_CONFIGS.data.featureConfigs,
            {
              uuid: 'demo-suppress-pricing',
              name: 'SUPPRESS_PRICING_SUGGESTIONS' as const,
              createdBy: 'demo',
              reason: '',
              created: '',
              updated: '',
            },
          ],
        },
      };

  const pricingSuggestions: PricingSuggestionsResponse = showPricing
    ? buildPricingSuggestions(resolved)
    : { data: { overallOptimalRateType: 'APR', supplierMarketGroups: [] } };

  return {
    supplier: buildDemoSupplier(resolved),
    supplierMarkets: buildSupplierMarkets(resolved),
    exchangeRates: DEMO_EXCHANGE_RATES,
    featureConfigs,
    offers: buildOffers(resolved),
    playableInvoices: buildPlayableInvoices(resolved),
    playableInvoiceStats: buildPlayableInvoiceStats(resolved),
    pricingSuggestions,
    userSuppliers: buildUserSuppliers(resolved),
    userTerms: buildUserTerms(resolved),
    invoiceRules: buildInvoiceRules(resolved),
    lockedInvoices: buildLockedInvoices(resolved),
    lockedInvoiceStats: buildLockedInvoiceStats(resolved),
    toggleOfferStatus: { data: {} },
    supplierMarketClears: buildSupplierMarketClears(resolved),
    creditNotes: buildCreditNotes(resolved),
    recentClears: buildRecentClears(resolved),
    supplierMarketClearsStats: buildSupplierMarketClearsStats(resolved),
    supplierAgreements: [],
    priceDiscoveryPost: { data: [] },
    priceDiscoveryPatch: {
      data: {
        rebundleResultUuid: 'demo-rebundle-uuid',
        offers: [],
        status: 'PENDING',
      },
    },
    userPreferences: { data: { features: { autoCreateOffersForNewBuyers: null } } },
    buyersExpansion: { data: [] },
    buyersLinkingRequests: { data: [] },
    buyers: { data: [] },
    adjustmentInvoiceStats: {
      data: { makerOrganizationUuid: '', summary: { amount: 0, count: 0 }, supplierMarkets: [] },
    },
    artifacts: buildMarketingArtifacts(resolved),
    dismissibles: { data: [] },
    clearSchedules: { data: { supplierMarketClearSchedulesGroups: [] } },
    buyersExpansionTrending: { data: [] },
    buyersLending: { data: [] },
    userLendingOpportunities: { data: { opportunities: [] } },
    adjustmentInvoices: { data: { invoices: [], pageInfo: { hasNextPage: false } } },
    payDateAnnouncements: { data: [] },
    preferredAutoRenewal: {
      data: {
        uuid: '',
        offerType: 'preferred-term',
        frequency: 'recurring',
        status: 'active',
        excludeNewInvoices: false,
        expirationDate: null,
        supplierMarkets: [],
        editRestrictions: {
          isDeletable: false,
          isInvoicesEditable: false,
          isRateEditable: false,
          isStatusToggleable: false,
        },
        rateInformation: { rate: 0, rateType: 'APR', type: 'preferred' },
        schedules: [],
      },
    },
    buyerCashflow: {
      data: {
        aggregatedAt: '',
        currency: resolved.supplier.currency,
        includesCurrencies: [resolved.supplier.currency],
        datacentersQueried: [],
        totals: {
          totalInvoiceCount: 0,
          totalInvoiceAmount: 0,
          totalDeductionCount: 0,
          totalDeductionAmount: 0,
          paidOnTermsCount: 0,
          paidOnTermsAmount: 0,
          c2foEarlyPaidCount: 0,
          c2foEarlyPaidAmount: 0,
          c2foEarlyPaidDiscount: 0,
          futureDueCount: 0,
          futureDueAmount: 0,
          pastDueCount: 0,
          pastDueAmount: 0,
          incomingCashCount: 0,
          incomingCashAmount: 0,
          collectedCount: 0,
          collectedAmount: 0,
          remainingCount: 0,
          remainingAmount: 0,
        },
        byCustomer: [],
        byPeriod: { byDay: [] },
      },
    },
  };
}
